import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  AlertTriangle,
  Ban,
  Bookmark,
  Check,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Circle,
  Compass,
  GitBranch,
  Lightbulb,
  Mic,
  Shield,
  Sparkles,
  Target,
  User,
} from 'lucide-react'
import { decodeHtmlEntities, HIDDEN_PRIVATE_NOTIFICATION_CODES } from './liveControlsUtils'

// ── Coaching Cues (from companion view, ex wrap_up) ───────────

const CUE_CATEGORY_META = {
  follow_up: { label: 'Follow up', tone: 'border-purple-200 bg-purple-50 text-purple-900' },
  summarize: { label: 'Summarize', tone: 'border-blue-200 bg-blue-50 text-blue-900' },
  engage:    { label: 'Engage',    tone: 'border-emerald-200 bg-emerald-50 text-emerald-900' },
  decide:    { label: 'Decide',    tone: 'border-orange-200 bg-orange-50 text-orange-900' },
  // wrap_up intentionally omitted — for Teams/Meet there is no upfront
  // agenda budget to wrap against, so the cue is meaningless. Hosted Zoom
  // agendas can re-introduce it in a follow-up if needed.
}

function CoachingCues({ cues = [] }) {
  const filtered = (cues || []).filter((c) => CUE_CATEGORY_META[c?.category])
  if (filtered.length === 0) return null
  // Latest-first, max 3 so it doesn't crowd the column.
  const ordered = [...filtered].sort((a, b) => (b.createdAtMs ?? 0) - (a.createdAtMs ?? 0)).slice(0, 3)
  return (
    <section className="rounded-2xl border border-slate-200/80 bg-white/80 p-4 shadow-sm">
      <div className="flex items-center gap-2.5 mb-3">
        <Sparkles className="h-4.5 w-4.5 text-violet-500" />
        <h4 className="text-sm font-bold text-slate-900">Coaching cues</h4>
      </div>
      <div className="space-y-2">
        {ordered.map((cue, i) => {
          const meta = CUE_CATEGORY_META[cue.category]
          return (
            <div
              key={cue.cueId ?? `${cue.category}:${i}`}
              className={`rounded-lg border px-3 py-2 text-sm ${meta.tone}`}
            >
              <div className="flex items-baseline gap-2">
                <span className="text-[10px] uppercase tracking-wider opacity-70">{meta.label}</span>
                <span className="flex-1 font-medium">{cue.message}</span>
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}

// ── Where We Are (rolling 3-bullet recap) ─────────────────────

function formatMinutesAgo(ms) {
  if (!ms) return ''
  const delta = Date.now() - ms
  const min = Math.max(0, Math.round(delta / 60_000))
  if (min < 1) return 'just now'
  if (min === 1) return '1 min ago'
  return `${min} min ago`
}

function WhereWeAre({ rollingSummary }) {
  if (!rollingSummary?.bullets?.length) return null
  return (
    <section className="rounded-2xl border border-slate-200/80 bg-white/80 p-4 shadow-sm">
      <div className="flex items-baseline justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <Compass className="h-4.5 w-4.5 text-sky-600" />
          <h4 className="text-sm font-bold text-slate-900">Where We Are</h4>
        </div>
        <span className="text-[10px] text-slate-400">{formatMinutesAgo(rollingSummary.updatedAtMs)}</span>
      </div>
      <ul className="ml-4 list-disc space-y-1.5 text-sm text-slate-700">
        {rollingSummary.bullets.map((b, i) => (
          <li key={i} className="leading-snug">{b}</li>
        ))}
      </ul>
    </section>
  )
}

// ── Parked (collapsible, voice-command-triggered) ─────────────

function Parked({ items = [] }) {
  const [open, setOpen] = useState(false)
  const list = Array.isArray(items) ? items : []
  if (list.length === 0) return null
  return (
    <section className="rounded-2xl border border-slate-200/80 bg-white/80 shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2.5 px-4 py-3"
        aria-expanded={open}
      >
        <Bookmark className="h-4.5 w-4.5 text-amber-600" />
        <h4 className="text-sm font-bold text-slate-900 flex-1 text-left">Parked</h4>
        <span className="text-xs font-medium text-slate-400 tabular-nums">{list.length}</span>
        {open ? (
          <ChevronDown className="h-4 w-4 text-slate-400" />
        ) : (
          <ChevronRight className="h-4 w-4 text-slate-400" />
        )}
      </button>
      {open ? (
        <ul className="border-t border-slate-100 px-4 py-3 space-y-1.5 text-sm text-slate-700">
          {list.map((item, i) => (
            <li key={item.topic ?? i} className="leading-snug">{item.topic ?? item}</li>
          ))}
        </ul>
      ) : null}
    </section>
  )
}

// ── Live Stats Row (interrupts · tangents · talk time) ────────

function LiveStatsRow({ interruptCount, tangentCount, speakingTimePct }) {
  const interruptLevel = interruptCount === 0 ? 'ok' : interruptCount < 3 ? 'warn' : 'alert'
  const tangentLevel = tangentCount === 0 ? 'ok' : tangentCount < 3 ? 'warn' : 'alert'

  const levelStyles = {
    ok:    { card: 'border-emerald-200/70 bg-emerald-50/50', num: 'text-emerald-600', icon: 'text-emerald-400' },
    warn:  { card: 'border-amber-200/80 bg-amber-50/60',     num: 'text-amber-600',   icon: 'text-amber-400' },
    alert: { card: 'border-red-200/80 bg-red-50/60',         num: 'text-red-600',     icon: 'text-red-400' },
  }

  const iStyles = levelStyles[interruptLevel]
  const tStyles = levelStyles[tangentLevel]

  return (
    <div className="grid grid-cols-3 gap-2.5">
      <div className={`rounded-xl border ${iStyles.card} px-3 py-3.5 flex flex-col items-center gap-1`}>
        <Ban className={`h-5 w-5 ${iStyles.icon}`} />
        <span className={`text-2xl font-bold tabular-nums leading-none ${iStyles.num}`}>{interruptCount}</span>
        <span className="text-xs text-slate-400 text-center leading-none mt-0.5">Interrupts</span>
      </div>
      <div className={`rounded-xl border ${tStyles.card} px-3 py-3.5 flex flex-col items-center gap-1`}>
        <GitBranch className={`h-5 w-5 ${tStyles.icon}`} />
        <span className={`text-2xl font-bold tabular-nums leading-none ${tStyles.num}`}>{tangentCount}</span>
        <span className="text-xs text-slate-400 text-center leading-none mt-0.5">Tangents</span>
      </div>
      <div className="rounded-xl border border-accent-200/70 bg-accent-50/50 px-3 py-3.5 flex flex-col items-center gap-1">
        <Mic className="h-5 w-5 text-accent-500" />
        <span className="text-2xl font-bold tabular-nums leading-none text-accent-700">
          {speakingTimePct != null ? `${speakingTimePct}%` : '—'}
        </span>
        <span className="text-xs text-slate-400 text-center leading-none mt-0.5">Talk Time</span>
      </div>
    </div>
  )
}

// ── Private Goals ─────────────────────────────────────────────

function PrivateGoals({ memberPrivateGoals }) {
  if (memberPrivateGoals.length === 0) return null

  const total = memberPrivateGoals.length
  const coveredCount = memberPrivateGoals.filter(
    (g) => g.lifecycleStatus === 'covered' || g.lifecycleStatus === 'covered_weak_confidence'
  ).length

  return (
    <section className="rounded-2xl border border-accent-200/80 bg-accent-50/40 p-5 shadow-sm">
      <div className="flex items-center gap-2.5 mb-3.5">
        <Target className="h-5 w-5 text-accent-600" />
        <h4 className="text-base font-bold text-slate-900">Your Goals</h4>
        {coveredCount > 0 ? (
          <span className="text-xs font-medium text-slate-400 tabular-nums">{coveredCount}/{total}</span>
        ) : null}
      </div>
      <div className="space-y-2.5">
        {memberPrivateGoals.map((goal) => {
          const isCovered = goal.lifecycleStatus === 'covered' || goal.lifecycleStatus === 'covered_weak_confidence'
          const isAtRisk = goal.lifecycleStatus === 'at_risk'

          return (
            <div key={goal.internalAgendaItemId} className="flex items-start gap-3 py-1">
              {isCovered ? (
                <Check className="h-4 w-4 shrink-0 mt-0.5 text-emerald-500" />
              ) : isAtRisk ? (
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-red-500" />
              ) : (
                <Circle className="h-4 w-4 shrink-0 mt-0.5 text-slate-300" />
              )}
              <p className={`text-sm leading-5 ${isCovered ? 'text-slate-500' : 'text-slate-900'}`}>
                {decodeHtmlEntities(goal.statusSummary || goal.title || '')}
              </p>
            </div>
          )
        })}
      </div>
    </section>
  )
}

// ── Never-Say Tags Reminder ──────────────────────────────────

function NeverSayReminder({ memberPrivateGoalsState }) {
  if (!memberPrivateGoalsState?.items) return null

  const allTags = []
  for (const item of memberPrivateGoalsState.items) {
    if (Array.isArray(item.neverSayTags)) {
      for (const tag of item.neverSayTags) {
        if (tag && !allTags.includes(tag)) allTags.push(tag)
      }
    }
  }

  if (allTags.length === 0) return null

  return (
    <div className="rounded-lg border border-red-200/60 bg-red-50/30 px-4 py-2.5">
      <p className="text-sm font-medium text-red-500">
        Avoid: {allTags.join(', ')}
      </p>
    </div>
  )
}

// ── Your Tone ─────────────────────────────────────────────────

const TONE_STYLE = {
  constructive:  { bar: 'bg-emerald-400', label: 'Constructive', chip: 'bg-emerald-100 text-emerald-700', text: 'text-emerald-700' },
  neutral:       { bar: 'bg-slate-300',   label: 'Neutral',      chip: 'bg-slate-100 text-slate-600',    text: 'text-slate-600'   },
  negative:      { bar: 'bg-amber-400',   label: 'Negative',     chip: 'bg-amber-100 text-amber-700',    text: 'text-amber-700'   },
  contentious:   { bar: 'bg-orange-400',  label: 'Contentious',  chip: 'bg-orange-100 text-orange-700',  text: 'text-orange-700'  },
  argumentative: { bar: 'bg-red-400',     label: 'Argumentative',chip: 'bg-red-100 text-red-700',        text: 'text-red-700'     },
}

const CONCERNING_STATUSES = new Set(['negative', 'contentious', 'argumentative'])

// Multi-label tone chip color by intensity (Tone Engine V2).
const TONE_CHIP_BY_INTENSITY = {
  high: 'bg-red-100 text-red-700 border border-red-200',
  medium: 'bg-amber-100 text-amber-700 border border-amber-200',
  low: 'bg-slate-100 text-slate-600 border border-slate-200',
}

function MeetingTone({ viewerToneSignal, viewerToneBreakdown = [] }) {
  const currentStatus = viewerToneSignal?.status ?? null
  const currentStyle = currentStatus ? (TONE_STYLE[currentStatus] ?? TONE_STYLE.neutral) : null
  const isConcerning = currentStatus && CONCERNING_STATUSES.has(currentStatus)
  const hasBreakdown = viewerToneBreakdown.length > 0

  // V2 multi-label vector (frustrated / exasperated / impatient / ...). Present
  // only when TONE_ENGINE_V2_ENABLED=on; falls back to the single status chip.
  const liveTones = Array.isArray(viewerToneSignal?.tones) ? viewerToneSignal.tones : []
  const headerLabel = liveTones[0]?.label ?? currentStyle?.label ?? null

  const sectionBorder = isConcerning
    ? 'border-amber-200/80 bg-amber-50/40'
    : 'border-primary-200/60 bg-primary-50/30'

  return (
    <section className={`rounded-2xl border ${sectionBorder} p-5 shadow-sm`}>
      <div className="flex items-center gap-2.5 mb-3.5">
        <Mic className={`h-5 w-5 ${isConcerning ? 'text-amber-600' : 'text-primary-500'}`} />
        <h4 className="text-base font-bold text-slate-900">Your Tone</h4>
        {headerLabel ? (
          <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${currentStyle?.chip ?? TONE_STYLE.neutral.chip}`}>
            {headerLabel}
          </span>
        ) : null}
      </div>

      {liveTones.length > 0 ? (
        <div className="mb-3.5 flex flex-wrap gap-1.5">
          {liveTones.map((t) => (
            <span
              key={t.id}
              className={`rounded-full px-2.5 py-1 text-xs font-medium ${TONE_CHIP_BY_INTENSITY[t.intensity] ?? TONE_CHIP_BY_INTENSITY.low}`}
              title={`${t.label} · ${Math.round((t.score ?? 0) * 100)}%`}
            >
              {t.label}
            </span>
          ))}
        </div>
      ) : null}

      {hasBreakdown ? (
        <div className="space-y-2.5">
          {viewerToneBreakdown.map((entry) => {
            const style = TONE_STYLE[entry.status] ?? TONE_STYLE.neutral
            return (
              <div key={entry.status}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className={`text-sm font-medium ${style.text}`}>{style.label}</span>
                  <span className="text-xs tabular-nums text-slate-400">{entry.pct}%</span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${style.bar} transition-[width] duration-500`}
                    style={{ width: `${entry.pct}%` }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <p className="text-sm text-slate-400">Tone tracking will appear once you start speaking.</p>
      )}

      {isConcerning && viewerToneSignal?.coachingTip ? (
        <p className="mt-3 pt-3 border-t border-amber-200/60 text-sm text-slate-600 leading-5">
          {decodeHtmlEntities(viewerToneSignal.coachingTip)}
        </p>
      ) : null}
    </section>
  )
}

// ── Coaching Nudges (from privateNotifications) ─────────────

const NUDGE_META = {
  interrupt_warning:           { label: 'Interruption detected' },
  interrupt_soft_mute:         { label: 'You were briefly muted for interrupting' },
  interrupt_hard_mute:         { label: 'Muted — too many interruptions' },
  tangent_nudge:               { label: 'Getting off-topic' },
  tangent_escalation:          { label: 'Still off-topic — consider refocusing' },
  overtalk_nudge:              { label: "You've been speaking a while — consider pausing" },
  time_warning_while_speaking: { label: 'Time running low on this topic' },
  wrapup_while_speaking:       { label: 'Time to wrap up' },
  overtime_while_speaking:     { label: 'Over time — wrap up now' },
  organizer_interrupt_alert:   { label: 'Interruption detected' },
  organizer_mute_alert:        { label: 'Participant muted' },
  organizer_tangent_alert:     { label: 'Off-topic nudge sent' },
  organizer_overtalk_alert:    { label: 'Talk equity alert' },
}

const ORGANIZER_NUDGE_CODES = new Set([
  'organizer_interrupt_alert',
  'organizer_mute_alert',
  'organizer_tangent_alert',
  'organizer_overtalk_alert',
])

function CoachingNudges({ privateNotifications }) {
  if (!privateNotifications?.length) return null

  const personalNudges = privateNotifications.filter(
    (n) => !ORGANIZER_NUDGE_CODES.has(n.code) && !HIDDEN_PRIVATE_NOTIFICATION_CODES.has(n.code)
  )

  const latestByCode = new Map()
  for (const n of personalNudges) {
    const existing = latestByCode.get(n.code)
    if (!existing || n.createdAtMs > existing.createdAtMs) {
      latestByCode.set(n.code, n)
    }
  }
  const nudges = [...latestByCode.values()]
    .sort((a, b) => b.createdAtMs - a.createdAtMs)
    .slice(0, 3)

  if (nudges.length === 0) return null

  return (
    <div className="space-y-2.5">
      {nudges.map((n) => {
        const meta = NUDGE_META[n.code]
        const isAlert = n.severity === 'alert'
        return (
          <div
            key={n.notificationId}
            className={`flex items-center gap-3 rounded-lg px-3 py-2.5 ${
              isAlert
                ? 'border border-amber-200/60 bg-amber-50/30'
                : 'border border-slate-200/60 bg-slate-50/30'
            }`}
          >
            <AlertTriangle className={`h-4 w-4 shrink-0 ${isAlert ? 'text-amber-400' : 'text-slate-400'}`} />
            <p className={`text-sm font-medium ${isAlert ? 'text-amber-700' : 'text-slate-600'}`}>
              {meta?.label ?? n.title}
            </p>
          </div>
        )
      })}
    </div>
  )
}

// ── Active governance mute badges (organizer/co-host) ─────────

function GovernanceMuteBadges({ badges = [], onPersonalAction, readOnly = false }) {
  if (!badges.length) return null

  return (
    <section className="rounded-2xl border border-red-200/80 bg-red-50/40 p-5 shadow-sm">
      <motion.div className="flex items-center gap-2.5 mb-3.5">
        <Shield className="h-5 w-5 text-red-600" />
        <h4 className="text-base font-bold text-slate-900">Participant muted</h4>
        <span className="text-xs font-medium text-red-500">{badges.length}</span>
      </motion.div>
      <motion.div className="space-y-2.5">
        {badges.map((badge) => (
          <div
            key={badge.badgeId}
            className="flex items-center gap-3 rounded-lg border border-red-200/60 bg-white px-3 py-2.5"
          >
            <Shield className="h-4 w-4 shrink-0 text-red-500" />
            <p className="text-sm font-medium text-red-700 min-w-0 flex-1">
              {decodeHtmlEntities(badge.participantDisplayName ?? badge.participantId)} was{' '}
              {badge.muteKind === 'hard_mute' ? 'hard-muted' : 'soft-muted'}
            </p>
            {!readOnly ? (
              <button
                type="button"
                className="shrink-0 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
                onClick={() => onPersonalAction?.({ itemId: badge.badgeId, action: 'dismiss' })}
              >
                Dismiss
              </button>
            ) : null}
          </div>
        ))}
      </motion.div>
    </section>
  )
}

// ── Enforcement Activity (organizer/co-host) ──────────────────

function EnforcementActivity({ privateNotifications }) {
  if (!privateNotifications?.length) return null

  const organizerNudges = privateNotifications
    .filter((n) => ORGANIZER_NUDGE_CODES.has(n.code))
    .sort((a, b) => b.createdAtMs - a.createdAtMs)
    .slice(0, 8)

  if (organizerNudges.length === 0) return null

  return (
    <section className="rounded-2xl border border-orange-200/80 bg-orange-50/40 p-5 shadow-sm">
      <div className="flex items-center gap-2.5 mb-3.5">
        <Shield className="h-5 w-5 text-orange-600" />
        <h4 className="text-base font-bold text-slate-900">Enforcement Activity</h4>
        <span className="text-xs font-medium text-orange-500">{organizerNudges.length}</span>
      </div>
      <div className="space-y-2.5">
        {organizerNudges.map((n) => {
          const isAlert = n.severity === 'alert'
          return (
            <div
              key={n.notificationId}
              className={`flex items-center gap-3 rounded-lg px-3 py-2.5 border ${
                isAlert
                  ? 'border-orange-200/60 bg-white'
                  : 'border-amber-200/60 bg-white'
              }`}
            >
              <Shield className={`h-4 w-4 shrink-0 ${isAlert ? 'text-orange-500' : 'text-amber-400'}`} />
              <p className={`text-sm font-medium ${isAlert ? 'text-orange-700' : 'text-amber-700'}`}>
                {n.title}
              </p>
            </div>
          )
        })}
      </div>
    </section>
  )
}

// ── All Clear Fallback ──────────────────────────────────────

function AllClearMessage() {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-emerald-200/60 bg-emerald-50/30 px-4 py-3">
      <CheckCircle className="h-4 w-4 shrink-0 text-emerald-500" />
      <p className="text-sm text-slate-500">Meeting is on track.</p>
    </div>
  )
}

// ── Live Tips ─────────────────────────────────────────────────

function LiveTips({ privateTips }) {
  if (privateTips.length === 0) return null

  return (
    <section className="rounded-2xl border border-accent-200/80 bg-accent-50/40 p-5 shadow-sm">
      <div className="flex items-center gap-2.5 mb-3.5">
        <Lightbulb className="h-5 w-5 text-accent-600" />
        <h4 className="text-base font-bold text-slate-900">Tips</h4>
      </div>
      <div className="space-y-2.5">
        {privateTips.map((tip, i) => (
          <p key={i} className="text-sm leading-5 text-slate-700">{decodeHtmlEntities(tip)}</p>
        ))}
      </div>
    </section>
  )
}

// ── Main PersonalContextColumn ────────────────────────────────

export default function PersonalContextColumn({
  personalView,
  companionView = null,
  memberPrivateGoalsState = null,
  pulseBarVisible = false,
  onPersonalAction,
  readOnly = false,
}) {
  const view = personalView ?? {}
  const memberPrivateGoals = Array.isArray(view.memberPrivateGoals) ? view.memberPrivateGoals : []
  const viewerToneSignal = view.viewerToneSignal || null

  // Filter privateTips to exclude messages already shown in ActionRequiredBand (personal queue)
  const personalQueueMessages = new Set(
    (Array.isArray(view.personalQueue) ? view.personalQueue : [])
      .filter((item) => item.actionState?.status === 'open')
      .map((item) => item.message)
      .filter(Boolean)
  )
  const privateTips = (Array.isArray(view.privateTips) ? view.privateTips : [])
    .filter((tip) => !personalQueueMessages.has(tip))

  const privateNotifications = Array.isArray(view.privateNotifications) ? view.privateNotifications : []
  const activeGovernanceMuteBadges = Array.isArray(view.activeGovernanceMuteBadges)
    ? view.activeGovernanceMuteBadges
    : []
  const canSeeGovernanceMutes =
    view.viewerRole === 'meeting_owner' || activeGovernanceMuteBadges.length > 0

  const hasGoals = memberPrivateGoals.length > 0
  const hasTips = privateTips.length > 0
  const hasNudges = privateNotifications.length > 0
  // MeetingTone always renders, so there is always content
  const hasAnyContent = true || hasGoals || hasTips || hasNudges

  return (
    <div className="flex min-h-0 flex-col">
      {/* Sticky column header */}
      <div className={`sticky ${pulseBarVisible ? 'top-[52px]' : 'top-0'} z-10 flex items-center gap-3 rounded-t-xl border border-slate-200 bg-gradient-to-br from-accent-50/80 to-white/95 px-4 py-3.5 backdrop-blur-sm shadow-sm`}>
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-accent-100">
          <User className="h-4 w-4 text-accent-600" />
        </div>
        <h3 className="text-base font-bold text-slate-900">Your Private View</h3>
      </div>

      <div className="flex flex-col gap-5 rounded-b-xl border border-t-0 border-slate-200 bg-slate-50/30 p-4">
        {/* Top-of-column: prescriptive coaching cues. Filtered to follow_up
            / summarize / engage / decide; wrap_up suppressed for Teams/Meet
            since there's no upfront agenda budget to wrap against. */}
        <CoachingCues cues={companionView?.contextualCoachingCues ?? []} />
        <LiveStatsRow
          interruptCount={view.viewerInterruptCount ?? 0}
          tangentCount={view.viewerTangentCount ?? 0}
          speakingTimePct={view.viewerSpeakingTimePct ?? null}
        />
        <PrivateGoals memberPrivateGoals={memberPrivateGoals} />
        <NeverSayReminder memberPrivateGoalsState={memberPrivateGoalsState} />
        <MeetingTone
          viewerToneSignal={viewerToneSignal}
          viewerToneBreakdown={view.viewerToneBreakdown ?? []}
        />
        {/* Where We Are — rolling 3-bullet recap. Re-orient surface for
            users who zoned out for a stretch. Updated by liveCoachingService
            roughly every 2 minutes. */}
        <WhereWeAre rollingSummary={companionView?.rollingSummary ?? null} />
        {/* Parked — voice-command-triggered topic deferrals. Collapsible
            so it doesn't crowd the column when not in use. */}
        <Parked items={companionView?.parkingLot ?? []} />
        {canSeeGovernanceMutes ? (
          <GovernanceMuteBadges
            badges={activeGovernanceMuteBadges}
            onPersonalAction={onPersonalAction}
            readOnly={readOnly}
          />
        ) : null}
        {canSeeGovernanceMutes ? (
          <EnforcementActivity privateNotifications={privateNotifications} />
        ) : null}
        <CoachingNudges privateNotifications={privateNotifications} />
        <LiveTips privateTips={privateTips} />
        {!hasAnyContent ? <AllClearMessage /> : null}
      </div>
    </div>
  )
}
